// Probability & Random helpers: basic probability, combinatorics
// (combinations/permutations/factorial), classic discrete/continuous
// distributions (binomial/normal/Poisson), expected value, a generic
// random number generator, odds conversion, conditional probability, and
// dice-sum probability. Mostly pure math with no natural-language output —
// only the basic Probability Calculator gets a localized qualitative label.
import { t } from './i18n'

function factorial(n: number): bigint {
    let result = BigInt(1)
    for (let i = BigInt(2); i <= BigInt(n); i++) result *= i
    return result
}
function combinationBig(n: number, r: number): bigint {
    if (r > n || r < 0) return BigInt(0)
    return factorial(n) / (factorial(r) * factorial(n - r))
}
function permutationBig(n: number, r: number): bigint {
    if (r > n || r < 0) return BigInt(0)
    return factorial(n) / factorial(n - r)
}

// "Probability Calculator" - basic favorable/total probability, plus a
// localized qualitative read of how likely that is
const VERY_UNLIKELY: Record<string, string> = {
    en: 'Very Unlikely', ru: 'Крайне маловероятно', de: 'Sehr unwahrscheinlich', es: 'Muy Improbable', fr: 'Très Improbable', it: 'Molto Improbabile', pl: 'Bardzo Mało Prawdopodobne', lv: 'Ļoti Maz Ticams',
}
const UNLIKELY: Record<string, string> = {
    en: 'Unlikely', ru: 'Маловероятно', de: 'Unwahrscheinlich', es: 'Improbable', fr: 'Improbable', it: 'Improbabile', pl: 'Mało Prawdopodobne', lv: 'Maz Ticams',
}
const EVEN_CHANCE: Record<string, string> = {
    en: 'Roughly Even Chance', ru: 'Примерно равные шансы', de: 'Etwa gleiche Chance', es: 'Probabilidad Aproximadamente Igual', fr: 'Chances à Peu Près Égales', it: 'Probabilità Circa Uguale', pl: 'W Przybliżeniu Równe Szanse', lv: 'Aptuveni Vienādas Iespējas',
}
const LIKELY: Record<string, string> = {
    en: 'Likely', ru: 'Вероятно', de: 'Wahrscheinlich', es: 'Probable', fr: 'Probable', it: 'Probabile', pl: 'Prawdopodobne', lv: 'Ticams',
}
const VERY_LIKELY: Record<string, string> = {
    en: 'Very Likely', ru: 'Крайне вероятно', de: 'Sehr wahrscheinlich', es: 'Muy Probable', fr: 'Très Probable', it: 'Molto Probabile', pl: 'Bardzo Prawdopodobne', lv: 'Ļoti Ticams',
}
export function basicProbabilityCalculator(params: { favorable: unknown; total: unknown; language: unknown }): {
    probability: number; percentage: number; odds_for: string; interpretation: string
} {
    const favorable = Math.max(Number(params.favorable) || 0, 0)
    const total = Math.max(Number(params.total) || 1, 1)
    const p = Math.min(favorable / total, 1)
    const pct = p * 100
    let bucket: Record<string, string>
    if (pct < 10) bucket = VERY_UNLIKELY
    else if (pct < 35) bucket = UNLIKELY
    else if (pct < 65) bucket = EVEN_CHANCE
    else if (pct < 90) bucket = LIKELY
    else bucket = VERY_LIKELY
    const unfavorable = Math.max(total - favorable, 0)
    return { probability: p, percentage: pct, odds_for: `${favorable} : ${unfavorable}`, interpretation: t(bucket, params.language) }
}

// "Combination Calculator" (nCr) - returned as a string to preserve exact
// precision for large results well beyond double-precision safe range
export function combinationCalculator(params: { n: unknown; r: unknown }): { result: string } {
    const n = Math.min(Math.max(Math.round(Number(params.n)) || 0, 0), 1000)
    const r = Math.min(Math.max(Math.round(Number(params.r)) || 0, 0), n)
    return { result: combinationBig(n, r).toString() }
}

// "Permutation Calculator" (nPr)
export function permutationCalculator(params: { n: unknown; r: unknown }): { result: string } {
    const n = Math.min(Math.max(Math.round(Number(params.n)) || 0, 0), 1000)
    const r = Math.min(Math.max(Math.round(Number(params.r)) || 0, 0), n)
    return { result: permutationBig(n, r).toString() }
}

// "Factorial Calculator"
export function factorialCalculator(params: { n: unknown }): { result: string } {
    const n = Math.min(Math.max(Math.round(Number(params.n)) || 0, 0), 1000)
    return { result: factorial(n).toString() }
}

// "Binomial Probability Calculator" - exact P(X=k) plus cumulative P(X<=k)
export function binomialProbabilityCalculator(params: { trials: unknown; probability: unknown; successes: unknown }): {
    probability_exact: number; probability_cumulative: number
} {
    const n = Math.min(Math.max(Math.round(Number(params.trials)) || 0, 0), 1000)
    const p = Math.min(Math.max(Number(params.probability) || 0, 0), 1)
    const k = Math.min(Math.max(Math.round(Number(params.successes)) || 0, 0), n)
    const pmf = (i: number) => Number(combinationBig(n, i)) * Math.pow(p, i) * Math.pow(1 - p, n - i)
    let cumulative = 0
    for (let i = 0; i <= k; i++) cumulative += pmf(i)
    return { probability_exact: pmf(k), probability_cumulative: cumulative }
}

// "Normal Distribution Calculator" - probability below/above a value,
// using the Abramowitz & Stegun erf approximation (max error ~1.5e-7)
function erf(x: number): number {
    const sign = x >= 0 ? 1 : -1
    x = Math.abs(x)
    const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741, a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911
    const t = 1 / (1 + p * x)
    const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x)
    return sign * y
}
function normalCDF(x: number, mean: number, sd: number): number {
    const z = (x - mean) / (sd * Math.sqrt(2))
    return 0.5 * (1 + erf(z))
}
export function normalDistributionCalculator(params: { value: unknown; mean: unknown; standard_deviation: unknown }): {
    probability_below: number; probability_above: number
} {
    const value = Number(params.value) || 0
    const mean = Number(params.mean) || 0
    const sd = Number(params.standard_deviation) || 1
    const below = normalCDF(value, mean, sd)
    return { probability_below: below, probability_above: 1 - below }
}

// "Poisson Distribution Calculator" - exact P(X=k) plus cumulative P(X<=k)
export function poissonDistributionCalculator(params: { lambda: unknown; occurrences: unknown }): {
    probability_exact: number; probability_cumulative: number
} {
    const lambda = Math.max(Number(params.lambda) || 0, 0)
    const k = Math.min(Math.max(Math.round(Number(params.occurrences)) || 0, 0), 1000)
    const pmf = (i: number) => (Math.pow(lambda, i) * Math.exp(-lambda)) / Number(factorial(i))
    let cumulative = 0
    for (let i = 0; i <= k; i++) cumulative += pmf(i)
    return { probability_exact: pmf(k), probability_cumulative: cumulative }
}

// "Expected Value Calculator" - pairs values[i] with probabilities[i]
export function expectedValueCalculator(params: { values_list: unknown; probabilities_list: unknown }): { expected_value: number } {
    const parseList = (raw: unknown) =>
        String(raw || '').split(/[,\s\n]+/).map((s) => s.trim()).filter((s) => s.length > 0).map(Number)
    const values = parseList(params.values_list)
    const probabilities = parseList(params.probabilities_list)
    const n = Math.min(values.length, probabilities.length)
    let sum = 0
    for (let i = 0; i < n; i++) sum += values[i] * probabilities[i]
    return { expected_value: sum }
}

// "Random Number Generator" - generic min-max generator, distinct from the
// lottery/bingo pickers (which draw from fixed themed pools)
export function randomNumberGeneratorTool(params: {
    min: unknown; max: unknown; count: unknown; allow_duplicates: unknown; number_type: unknown
}): { generated_numbers: string } {
    const min = Number(params.min) || 0
    const max = Math.max(Number(params.max) || 0, min)
    const count = Math.min(Math.max(Math.round(Number(params.count)) || 1, 1), 1000)
    const allowDuplicates = String(params.allow_duplicates) === 'yes'
    const isInteger = String(params.number_type) !== 'decimal'
    const results: number[] = []
    if (allowDuplicates || !isInteger) {
        for (let i = 0; i < count; i++) {
            const v = isInteger ? Math.floor(Math.random() * (max - min + 1)) + min : Math.random() * (max - min) + min
            results.push(isInteger ? v : Math.round(v * 100) / 100)
        }
    } else {
        const pool: number[] = []
        for (let i = min; i <= max; i++) pool.push(i)
        for (let i = 0; i < count && pool.length > 0; i++) {
            const idx = Math.floor(Math.random() * pool.length)
            results.push(pool.splice(idx, 1)[0])
        }
    }
    return { generated_numbers: results.join(', ') }
}

// "Odds Converter" - converts between implied probability and betting-style
// odds ("X : Y" ratio plus decimal odds)
function gcd(a: number, b: number): number {
    a = Math.abs(a)
    b = Math.abs(b)
    while (b) {
        ;[a, b] = [b, a % b]
    }
    return a || 1
}
export function oddsConverter(params: { mode: unknown; probability_percent: unknown; odds_for: unknown; odds_against: unknown }): {
    probability_percent: number; odds_ratio: string; decimal_odds: number
} {
    const mode = String(params.mode)
    if (mode === 'odds_to_probability') {
        const oddsFor = Math.max(Number(params.odds_for) || 1, 0)
        const oddsAgainst = Math.max(Number(params.odds_against) || 1, 0)
        const total = oddsFor + oddsAgainst
        const p = total > 0 ? oddsFor / total : 0
        const g = gcd(oddsFor, oddsAgainst)
        return {
            probability_percent: p * 100,
            odds_ratio: `${oddsFor / g} : ${oddsAgainst / g}`,
            decimal_odds: p > 0 ? 1 / p : 0,
        }
    }
    const probabilityPercent = Math.min(Math.max(Number(params.probability_percent) || 0, 0.0001), 100)
    const p = probabilityPercent / 100
    const against = 1 - p
    const scale = 1000000
    let a = Math.round(p * scale)
    let b = Math.round(against * scale)
    const g = gcd(a, b)
    a = a / g
    b = b / g
    return {
        probability_percent: probabilityPercent,
        odds_ratio: `${a} : ${b}`,
        decimal_odds: p > 0 ? 1 / p : 0,
    }
}

// "Conditional Probability Calculator" - P(A|B) = P(A∩B) / P(B)
export function conditionalProbabilityCalculator(params: { p_a_and_b: unknown; p_b: unknown }): { p_a_given_b: number } {
    const pAandB = Math.min(Math.max(Number(params.p_a_and_b) || 0, 0), 1)
    const pB = Math.min(Math.max(Number(params.p_b) || 0, 0), 1)
    return { p_a_given_b: pB !== 0 ? Math.min(pAandB / pB, 1) : 0 }
}

// "Dice Roll Probability Calculator" - exact probability of a target sum
// with N dice, via dynamic-programming convolution (not brute-force
// enumeration, so it stays fast even for many dice)
export function diceRollProbabilityCalculator(params: { num_dice: unknown; sides: unknown; target_sum: unknown }): {
    ways_count: string; total_outcomes: string; probability_percent: number
} {
    const numDice = Math.min(Math.max(Math.round(Number(params.num_dice)) || 1, 1), 100)
    const sides = Math.min(Math.max(Math.round(Number(params.sides)) || 6, 2), 1000)
    const targetSum = Math.round(Number(params.target_sum)) || 0
    let ways = new Map<number, bigint>([[0, BigInt(1)]])
    for (let d = 0; d < numDice; d++) {
        const next = new Map<number, bigint>()
        for (const [sum, count] of ways.entries()) {
            for (let face = 1; face <= sides; face++) {
                const newSum = sum + face
                next.set(newSum, (next.get(newSum) || BigInt(0)) + count)
            }
        }
        ways = next
    }
    const totalOutcomes = BigInt(sides) ** BigInt(numDice)
    const waysForTarget = ways.get(targetSum) || BigInt(0)
    const probability = totalOutcomes > BigInt(0) ? Number(waysForTarget) / Number(totalOutcomes) : 0
    return { ways_count: waysForTarget.toString(), total_outcomes: totalOutcomes.toString(), probability_percent: probability * 100 }
}
